/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { SmallButton } from '../../../../../styles/ui/Button';
import { Field } from '../../../../../styles/forms/Field';
import {
   uploadExamDocument,
   getExamDocumentDownload,
} from '../../../../../services/hr/recruitment/onboardingApi';
import { recruitmentErrorMessage } from '../../utils/apiError';
import {
   UPLOAD_ACCEPTED_MIME_TYPES,
   validateUploadFile,
} from '../../utils/resume';

// ---------------------------------------------------------------------------
// Laudo do exame admissional (handoff 0068) — evidência anexada ao ONBOARDING,
// ao lado do seletor de resultado na etapa EXAME_ADMISSIONAL (e no modal de
// edição retroativa). Upload inline sem modal (fc7 cap.2 H.2.2), dispara na
// seleção do arquivo; com laudo → nome + Baixar (presigned) + Substituir.
// Renderiza como coluna do FormGrid (via slot children do ManualStepFields).
// ---------------------------------------------------------------------------

export default function ExamDocumentField({ onboarding, canAct, onChanged }) {
   const fileInputRef = useRef(null);
   const [isUploading, setIsUploading] = useState(false);
   const [isOpening, setIsOpening] = useState(false);

   const hasDocument = Boolean(onboarding?.has_admission_exam_document);

   const handleFileChange = async (event) => {
      const file = event.target.files?.[0] ?? null;
      // Permite re-selecionar o mesmo arquivo depois de um erro.
      event.target.value = '';
      if (!file || isUploading) return;

      const validationError = validateUploadFile(file);
      if (validationError) {
         toast.error(validationError);
         return;
      }

      setIsUploading(true);
      try {
         const { message } = await uploadExamDocument(onboarding.id, file);
         toast.success(message);
         onChanged?.();
      } catch (err) {
         toast.error(recruitmentErrorMessage(err, 'Erro ao anexar o laudo.'));
      } finally {
         setIsUploading(false);
      }
   };

   const handleDownload = async () => {
      if (isOpening) return;
      setIsOpening(true);
      try {
         const { url } = await getExamDocumentDownload(onboarding.id);
         if (!url) {
            toast.error('Não foi possível gerar o link do laudo.');
            return;
         }
         window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
         toast.error(recruitmentErrorMessage(err, 'Erro ao abrir o laudo.'));
      } finally {
         setIsOpening(false);
      }
   };

   return (
      <Field className="col-6">
         <label htmlFor="exam-document-upload">
            Laudo do exame (PDF, JPEG, PNG ou WEBP — máx. 10MB)
         </label>
         <div
            style={{
               display: 'flex',
               alignItems: 'center',
               gap: 8,
               flexWrap: 'wrap',
               minHeight: 40,
            }}
         >
            <input
               id="exam-document-upload"
               ref={fileInputRef}
               type="file"
               accept={UPLOAD_ACCEPTED_MIME_TYPES}
               style={{ display: 'none' }}
               onChange={handleFileChange}
            />

            {hasDocument ? (
               <>
                  <span
                     style={{
                        fontSize: '0.82rem',
                        color: '#374151',
                        overflowWrap: 'anywhere',
                     }}
                     title={onboarding?.admission_exam_file_original_name}
                  >
                     {onboarding?.admission_exam_file_original_name ?? 'laudo'}
                  </span>
                  <SmallButton
                     type="button"
                     disabled={isOpening}
                     onClick={handleDownload}
                  >
                     {isOpening ? 'Abrindo…' : 'Baixar'}
                  </SmallButton>
                  {canAct && (
                     <SmallButton
                        type="button"
                        $intent="write"
                        disabled={isUploading}
                        onClick={() => fileInputRef.current?.click()}
                     >
                        {isUploading ? 'Enviando…' : 'Substituir'}
                     </SmallButton>
                  )}
               </>
            ) : canAct ? (
               <SmallButton
                  type="button"
                  $intent="write"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
               >
                  {isUploading ? 'Enviando…' : 'Anexar laudo'}
               </SmallButton>
            ) : (
               <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
                  Nenhum laudo anexado.
               </span>
            )}
         </div>
      </Field>
   );
}

ExamDocumentField.propTypes = {
   onboarding: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      has_admission_exam_document: PropTypes.bool,
      admission_exam_file_original_name: PropTypes.string,
   }),
   canAct: PropTypes.bool,
   onChanged: PropTypes.func,
};
